import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { BellIcon, XIcon, CalendarIcon, BookOpenIcon, AlertCircleIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getTeacherTasks, Task } from '../services/taskService';
import { getActiveAlerts, Alert } from '../services/alertService';
import { subscribeLessons } from '../src/services/lessonsService';
import type { Lesson } from '../src/types/lesson';
interface NotificationCenterProps {
  darkMode?: boolean;
}
interface Notification {
  id: string;
  type: 'lesson' | 'task' | 'alert';
  icon: React.ReactNode;
  title: string;
  message: string;
  timestamp: Date;
  link?: string;
  urgency: number; // Higher = more urgent
}
export const NotificationCenter: React.FC<NotificationCenterProps> = ({
  darkMode = true
}) => {
  const {
    currentUser
  } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  // Format relative time with fresh "now"
  const formatRelativeTime = (date: Date): string => {
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);
    if (diffHours < -24) {
      return `${Math.abs(diffDays)} day${Math.abs(diffDays) !== 1 ? 's' : ''} ago`;
    } else if (diffHours < -1) {
      return `${Math.abs(diffHours)} hour${Math.abs(diffHours) !== 1 ? 's' : ''} ago`;
    } else if (diffHours < 0) {
      return 'recently';
    } else if (diffHours < 1) {
      return 'in less than an hour';
    } else if (diffHours < 24) {
      return `in ${diffHours} hour${diffHours !== 1 ? 's' : ''}`;
    } else {
      return `in ${diffDays} day${diffDays !== 1 ? 's' : ''}`;
    }
  };
  // Build complete notification list from all sources
  const buildNotifications = (tasks: Task[], alerts: Alert[], lessons: Lesson[]): Notification[] => {
    const now = new Date(); // Fresh "now" for accurate calculations
    const allNotifications: Notification[] = [];
    const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    // Process lessons
    lessons.forEach(lesson => {
      const lessonStart = new Date(lesson.startAt);
      const hoursUntil = (lessonStart.getTime() - now.getTime()) / (1000 * 60 * 60);
      // Upcoming lesson (within 24 hours)
      if (hoursUntil > 0 && hoursUntil <= 24) {
        allNotifications.push({
          id: `lesson-upcoming-${lesson.id}`,
          type: 'lesson',
          icon: <CalendarIcon className="h-5 w-5 text-green-500" data-id="element-2559" />,
          title: 'Upcoming Lesson',
          message: `${lesson.title || 'Lesson'} with ${lesson.studentName || 'student'} ${formatRelativeTime(lessonStart)}`,
          timestamp: lessonStart,
          link: '/teacher/calendar',
          urgency: hoursUntil < 2 ? 90 : 70
        });
      }
      // Cancelled lesson (last 7 days)
      if (lesson.status === 'CANCELED' && lessonStart > weekAgo) {
        allNotifications.push({
          id: `lesson-cancelled-${lesson.id}`,
          type: 'lesson',
          icon: <CalendarIcon className="h-5 w-5 text-red-500" data-id="element-2560" />,
          title: 'Lesson Cancelled',
          message: `${lesson.title || 'Lesson'} with ${lesson.studentName || 'student'} was cancelled`,
          timestamp: lessonStart,
          link: '/teacher/calendar',
          urgency: 50
        });
      }
    });
    // Process tasks
    tasks.forEach((task: Task) => {
      if (!task.dueDate || task.status === 'completed') return;
      const dueDate = new Date(task.dueDate);
      const hoursUntilDue = (dueDate.getTime() - now.getTime()) / (1000 * 60 * 60);
      // Overdue task
      if (hoursUntilDue < 0) {
        allNotifications.push({
          id: `task-overdue-${task.id}`,
          type: 'task',
          icon: <BookOpenIcon className="h-5 w-5 text-red-500" data-id="element-2561" />,
          title: 'Task Overdue',
          message: `"${task.title}" for ${task.studentName || 'student'} was due ${formatRelativeTime(dueDate)}`,
          timestamp: dueDate,
          link: '/assigned-tasks',
          urgency: 100
        });
      }
      // Due soon (within 48 hours)
      else if (hoursUntilDue <= 48) {
        allNotifications.push({
          id: `task-due-${task.id}`,
          type: 'task',
          icon: <BookOpenIcon className="h-5 w-5 text-amber-500" data-id="element-2562" />,
          title: 'Task Due Soon',
          message: `"${task.title}" for ${task.studentName || 'student'} is due ${formatRelativeTime(dueDate)}`,
          timestamp: dueDate,
          link: '/assigned-tasks',
          urgency: 80
        });
      }
    });
    // Process alerts
    alerts.forEach((alert: Alert) => {
      const alertDate = alert.createdAt?.toDate?.() || new Date();
      allNotifications.push({
        id: `alert-${alert.id}`,
        type: 'alert',
        icon: <AlertCircleIcon className="h-5 w-5 text-deep-pink" data-id="element-2563" />,
        title: 'Student Alert',
        message: `${alert.studentName}: ${alert.reason}`,
        timestamp: alertDate,
        link: `/reports?student=${alert.studentId}`,
        urgency: 60
      });
    });
    // Sort by urgency (desc) then timestamp (desc)
    const sorted = allNotifications.sort((a, b) => {
      if (a.urgency !== b.urgency) {
        return b.urgency - a.urgency;
      }
      return b.timestamp.getTime() - a.timestamp.getTime();
    });
    // Limit to 10
    return sorted.slice(0, 10);
  };
  // Fetch notifications with proper cleanup
  useEffect(() => {
    if (!currentUser?.uid) {
      setNotifications([]);
      setLoading(false);
      return;
    }
    let isMounted = true;
    let unsubscribeLessons: (() => void) | undefined;
    const fetchNotifications = async () => {
      try {
        setLoading(true);
        setError(null);
        const now = new Date();
        const tomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000);
        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        // Fetch tasks and alerts (one-time)
        const [tasks, alerts] = await Promise.all([getTeacherTasks(currentUser.uid), getActiveAlerts(currentUser.uid)]);
        if (!isMounted) return;
        // Subscribe to lessons (real-time)
        unsubscribeLessons = subscribeLessons({
          teacherId: currentUser.uid,
          from: weekAgo.toISOString(),
          to: tomorrow.toISOString(),
          onData: fetchedLessons => {
            if (!isMounted) return;
            // Rebuild complete notification list on each update
            const next = buildNotifications(tasks, alerts, fetchedLessons);
            setNotifications(next);
            setLoading(false);
          }
        });
      } catch (err) {
        console.error('Error fetching notifications:', err);
        if (isMounted) {
          setError("Couldn't load notifications. Please try again.");
          setLoading(false);
        }
      }
    };
    fetchNotifications();
    // Cleanup function
    return () => {
      isMounted = false;
      if (unsubscribeLessons) {
        unsubscribeLessons();
      }
    };
  }, [currentUser?.uid]);
  // Handle notification click
  const handleNotificationClick = (notification: Notification) => {
    if (notification.link) {
      navigate(notification.link);
    }
    setIsOpen(false);
  };
  // Close on ESC key (desktop only)
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !isMobile) {
        setIsOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, isMobile]);
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen && !isMobile) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, isMobile]);
  const unreadCount = notifications.length;
  const renderPanel = () => {
    if (isMobile) {
      // Mobile: Fixed bottom drawer with portal
      return createPortal(<>
          {/* Backdrop */}
          <div className="fixed inset-0 bg-black/50 z-[60]" onClick={() => setIsOpen(false)} data-id="element-2564" />

          {/* Drawer */}
          <div className="fixed bottom-0 left-0 right-0 z-[70] animate-slide-up" data-id="element-2565">
            <div className={`rounded-t-2xl shadow-2xl max-h-[70vh] overflow-hidden ${darkMode ? 'bg-navy-800' : 'bg-white'}`} onClick={e => e.stopPropagation()} data-id="element-2566">
              {/* Header */}
              <div className={`px-4 py-4 border-b ${darkMode ? 'border-navy-700' : 'border-gray-200'} flex justify-between items-center sticky top-0 ${darkMode ? 'bg-navy-800' : 'bg-white'} z-10`} data-id="element-2567">
                <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`} data-id="element-2568">
                  Notifications
                </h3>
                <button onClick={() => setIsOpen(false)} className={`p-2 rounded-lg ${darkMode ? 'hover:bg-navy-700' : 'hover:bg-gray-100'}`} data-id="element-2569">
                  <XIcon className={`h-5 w-5 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-2570" />
                </button>
              </div>

              {/* Content */}
              <div className="overflow-y-auto max-h-[calc(70vh-60px)]" data-id="element-2571">
                {loading ? <div className="px-4 py-8 text-center" data-id="element-2572">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-deep-pink mx-auto" data-id="element-2573"></div>
                  </div> : error ? <div className="px-4 py-8 text-center" data-id="element-2574">
                    <p className={`text-sm ${darkMode ? 'text-red-400' : 'text-red-600'}`} data-id="element-2575">
                      {error}
                    </p>
                  </div> : notifications.length > 0 ? <div data-id="element-2576">
                    {notifications.map(notification => <div key={notification.id} onClick={() => handleNotificationClick(notification)} className={`px-4 py-4 border-b ${darkMode ? 'border-navy-700' : 'border-gray-200'} active:bg-navy-600 transition-colors cursor-pointer`} data-id="element-2577">
                        <div className="flex gap-3" data-id="element-2578">
                          <div className={`rounded-full p-2 flex-shrink-0 ${darkMode ? 'bg-navy-700' : 'bg-gray-100'}`} data-id="element-2579">
                            {notification.icon}
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-2580">
                            <div className="flex justify-between items-start gap-2" data-id="element-2581">
                              <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-800'}`} data-id="element-2582">
                                {notification.title}
                              </p>
                              <span className={`text-xs whitespace-nowrap ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-2583">
                                {formatRelativeTime(notification.timestamp)}
                              </span>
                            </div>
                            <p className={`text-sm mt-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-2584">
                              {notification.message}
                            </p>
                          </div>
                        </div>
                      </div>)}
                  </div> : <div className="px-4 py-12 text-center" data-id="element-2585">
                    <div className={`rounded-full p-4 mx-auto w-fit mb-3 ${darkMode ? 'bg-navy-700' : 'bg-gray-100'}`} data-id="element-2586">
                      <BellIcon className="h-8 w-8 text-gray-400" data-id="element-2587" />
                    </div>
                    <p className={`text-base ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-2588">
                      No notifications yet
                    </p>
                  </div>}
              </div>
            </div>
          </div>
        </>, document.body);
    } else {
      // Desktop: Dropdown (not portaled, positioned relative to button)
      return <div ref={dropdownRef} className={`absolute right-0 mt-2 w-80 sm:w-96 rounded-lg shadow-lg py-1 z-50 ${darkMode ? 'bg-navy-800 border border-navy-700' : 'bg-white border border-gray-200'}`} data-id="element-2589">
          <div className={`px-4 py-3 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`} data-id="element-2590">
            <h3 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-800'}`} data-id="element-2591">
              Notifications
            </h3>
          </div>

          <div className="max-h-96 overflow-y-auto" data-id="element-2592">
            {loading ? <div className="px-4 py-6 text-center" data-id="element-2593">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-deep-pink mx-auto" data-id="element-2594"></div>
              </div> : error ? <div className="px-4 py-6 text-center" data-id="element-2595">
                <p className={`text-sm ${darkMode ? 'text-red-400' : 'text-red-600'}`} data-id="element-2596">
                  {error}
                </p>
              </div> : notifications.length > 0 ? <div data-id="element-2597">
                {notifications.map(notification => <div key={notification.id} onClick={() => handleNotificationClick(notification)} className={`block px-4 py-3 cursor-pointer hover:${darkMode ? 'bg-navy-700' : 'bg-gray-50'} border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`} data-id="element-2598">
                    <div className="flex" data-id="element-2599">
                      <div className={`rounded-full p-2 mr-3 ${darkMode ? 'bg-navy-700' : 'bg-gray-100'}`} data-id="element-2600">
                        {notification.icon}
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-2601">
                        <div className="flex justify-between items-start" data-id="element-2602">
                          <p className={`font-medium truncate ${darkMode ? 'text-white' : 'text-gray-800'}`} data-id="element-2603">
                            {notification.title}
                          </p>
                          <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'} ml-2 whitespace-nowrap`} data-id="element-2604">
                            {formatRelativeTime(notification.timestamp)}
                          </span>
                        </div>
                        <p className={`text-sm mt-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-2605">
                          {notification.message}
                        </p>
                      </div>
                    </div>
                  </div>)}
              </div> : <div className="px-4 py-6 text-center" data-id="element-2606">
                <div className={`rounded-full p-3 mx-auto w-fit mb-3 ${darkMode ? 'bg-navy-700' : 'bg-gray-100'}`} data-id="element-2607">
                  <BellIcon className="h-6 w-6 text-gray-400" data-id="element-2608" />
                </div>
                <p className={`${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-2609">
                  No notifications yet
                </p>
              </div>}
          </div>
        </div>;
    }
  };
  return <div className="relative" data-id="element-2610">
      <button onClick={() => setIsOpen(!isOpen)} className="relative p-1 rounded-full focus:outline-none focus:ring-2 focus:ring-deep-pink" aria-label="Notifications" data-id="element-2611">
        <BellIcon className="h-6 w-6 text-white" data-id="element-2612" />
        {unreadCount > 0 && <span className="absolute -top-1 -right-1 bg-deep-pink text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center" data-id="element-2613">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>}
      </button>

      {isOpen && renderPanel()}
    </div>;
};